/* ============================================================================
   olmia-ui.js — window.OlmiaUI: rendering + interactions (ported from design
   ui.js). Prices, cart count, mini-cart, drawer, header (search/menu/sticky),
   toasts, accordions, announce rotation, expert chat. No global product catalog:
   product cards are server-rendered by snippets/product-card.liquid.
   Cart state comes from window.OlmiaCart (Shopify Cart AJAX API).
   ========================================================================== */
window.OlmiaUI = (function () {
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const nlNum = (n) => new Intl.NumberFormat('nl-NL').format(n);

  /* ==========================================================================
     PRICE RULE — DE ENIGE GELDFORMATTER VAN DIT THEMA (besluit Sean 2026-08-21)
     --------------------------------------------------------------------------
     Notatie: CENTEN TONEN ALS ZE ER ZIJN, WEGLATEN ALS ZE NUL ZIJN.
       22.060 cent  ->  "€ 220,60"   (de klant betaalt écht 220,60)
       5.290.000 cent -> "52.900 euro" zonder komma-nullen — geen ",00"

     Waarom niet "altijd twee decimalen": het ontwerp schrijft ronde bedragen
     zelf zonder decimalen (250 / 2.500 / 52.000 euro; geen ",00", geen ",-"),
     en gemeten op 2026-08-21 heeft 27,5% van de kaartprijzen (39 van 142 met
     een prijs) centen terwijl álle 41 producten boven de 10.000 euro rond zijn.
     Een verplichte ",00" zou dus juist de duurste, meest zichtbare bedragen
     ontsieren zonder één cent extra waarheid te leveren.
     Waarom niet "0 decimalen" (de oude regel): 22.060 cent werd "€ 221" — een
     bedrag dat de winkel nooit incasseert. Dát was de aanleiding.

     Rekenen gebeurt in HELE CENTEN. Nooit een tussentijdse afronding, nooit
     een som van reeds afgeronde bedragen: `Money.sumCents([...])` telt op in
     integers en pas het eindresultaat gaat door `Money.fromCents`.
     Een som van afgeronde bedragen wijkt af van het afgeronde totaal — dat is
     precies de fout die op 2026-08-20 € 2 verschil opleverde.

     KOPIEER DEZE FORMATTER NIET. Elk ander bestand dat een bedrag toont hoort
     `OlmiaUI.Money` te gebruiken; drie kopieën betekent vroeg of laat drie
     oppervlakken met drie verschillende bedragen.
     ========================================================================== */
  const NF_EUR_2 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const NF_EUR_0 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  /* euro's (number of string, punt óf komma als decimaalteken) → hele centen.
     `220.6 * 100` is in drijvende komma 22059.999999999996; de afronding hier
     is dus geen kosmetiek maar de enige manier om aan 22060 te komen. */
  const toCents = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v == null ? '' : v).trim().replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  };
  /* hele centen → de zichtbare tekst. Dit is het ENIGE punt in het thema waar
     een bedrag tekst wordt. */
  const fromCents = (c) => {
    const n = Math.round(Number(c) || 0);
    return (n % 100 === 0) ? NF_EUR_0.format(n / 100) : NF_EUR_2.format(n / 100);
  };
  const Money = {
    cents: toCents,                                   // euro's  → centen
    fromCents,                                        // centen  → tekst
    format: (eur) => fromCents(toCents(eur)),         // euro's  → tekst
    /* Telt een lijst euro-bedragen op ZONDER tussentijds af te ronden. */
    sumCents: (list) => (list || []).reduce((a, v) => a + toCents(v), 0),
  };

  /* Attribuutveilige escape voor waarden die in gegenereerde HTML belanden. */
  const esc = (v) => String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  const ICON = {
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M12 5v14M5 12h14"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M20 6 9 17l-5-5"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 21s-7-4.5-9.5-9A4.6 4.6 0 0 1 12 6a4.6 4.6 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>',
    cartBig: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6h15l-1.5 9h-12L5 3H2"/><circle cx="9" cy="20" r="1.6"/><circle cx="18" cy="20" r="1.6"/></svg>',
  };

  /* ---------- price painting ---------- */
  /* ACTIEPRIJS — berekend in HELE CENTEN, nooit op een al afgerond bedrag.
     10% van € 220,60 is € 22,06; wie eerst afrondt naar € 221 en dan rekent komt op
     € 22,10 uit en toont een bedrag dat de kassa niet in rekening brengt. Money.cents
     en Money.fromCents zijn hier dus geen nettigheid maar de hele reden.
     Afronding: Math.round op hele centen, dezelfde kant op als Shopify.

     Dit TOONT alleen wat de kassa gaat doen; de korting zelf komt van een
     automatische korting in de Shopify-admin. Lopen die twee uiteen, dan ziet de
     klant een ander bedrag dan hij betaalt. `scripts/verify-actie.mjs` legt het
     getoonde bedrag daarom naast wat de winkelwagen werkelijk berekent. */
  function actiePrijs(price, pct) {
    const c = Money.cents(price);
    if (!(c > 0) || !(pct > 0)) return null;
    return Money.fromCents(Math.round(c * (100 - pct) / 100));
  }

  function priceInner(price, cust, actiePct, actieLabel) {
    /* Een klantprijs (b2b) wint van een actie: die is al onderhandeld en de klant
       hoort niet twee kortingen op elkaar te zien staan. */
    if (cust != null) {
      return `<span class="price__now">${Money.format(price)}</span>` +
        `<span class="price__cust"><span class="lbl">${T('cart.your_price', 'Jouw prijs')}</span><b>${Money.format(cust)}</b></span>`;
    }
    const nu = actiePct ? actiePrijs(price, actiePct) : null;
    if (nu) {
      return `<span class="price__was">${Money.format(price)}</span>` +
        `<span class="price__now price__now--actie">${nu}</span>` +
        (actieLabel ? `<span class="price__actie">${actieLabel} ${actiePct}%</span>` : '');
    }
    return `<span class="price__now">${Money.format(price)}</span>`;
  }
  function paintPrices(scope = document) {
    // Customer ("Jouw prijs") pricing only paints for a real B2B customer. The
    // signal is server-set: layout/theme.liquid stamps <body data-b2b="1"> only
    // when customer.tags contains 'b2b'. data-cust is likewise only ever emitted
    // server-side for B2B customers, so this is defence in depth on top of that.
    const b2b = document.body.dataset.b2b === '1';
    /* De attribuutwaarde gaat als STRING naar Money: `Money.cents` maakt er hele
       centen van en accepteert zowel `220.6` (Liquid `divided_by: 100.0`) als
       `220,6` (een merchant die een metaveld met een komma invult). `+el.dataset…`
       zou dat laatste stil in NaN veranderen. */
    $$('.price[data-price]', scope).forEach(el => {
      const price = el.dataset.price;
      const cust = (b2b && el.dataset.cust !== undefined && el.dataset.cust !== '') ? el.dataset.cust : null;
      const pct = Number(el.dataset.actiePct || 0);
      el.classList.toggle('is-cust', cust != null);
      el.classList.toggle('is-actie', cust == null && pct > 0);
      el.innerHTML = priceInner(price, cust, pct, el.dataset.actieLabel || '');
    });
    $$('[data-money]', scope).forEach(el => {
      el.textContent = Money.format(el.dataset.money);
    });
  }

  /* ---------- prijsmuur in de lade / mini-cart ----------
     /cart.js kent geen metavelden, dus de tier komt van window.OlmiaCart, die hem
     server-side opgehaald heeft (snippets/cart-drawer.liquid bij het laden, de
     meegestuurde sectie bij elke mutatie) en fail closed staat voor een regel die
     de server nog niet beoordeeld heeft. Zie de kop van olmia-cart.js.
     Ontbreekt OlmiaCart of is hij ouder dan dit bestand, dan geldt óók dicht:
     liever "Prijs op aanvraag" bij een gewoon product dan één bedrag te veel. */
  function gateOf(item) {
    return (window.OlmiaCart && OlmiaCart.gateFor) ? OlmiaCart.gateFor(item) : 'quote';
  }
  function cartGate(items) {
    return (window.OlmiaCart && OlmiaCart.worstGate) ? OlmiaCart.worstGate(items) : 'quote';
  }
  function gateCopy(state) {
    return (window.OlmiaCart && OlmiaCart.copyFor)
      ? OlmiaCart.copyFor(state)
      : { label: T('common.price_on_request', 'Prijs op aanvraag'), href: '/pages/contact', cta: T('common.request_quote', 'Offerte aanvragen'), note: '' };
  }
  function gateLink(state, cls) {
    const c = gateCopy(state);
    return `<a class="${cls}" href="${esc(c.href)}">${esc(c.label)}</a>`;
  }

  /* ---------- cart line (Shopify /cart.js line item) ---------- */
  function lineMarkup(item, mini) {
    const nameOnly = item.product_title || item.title;
    const variantLabel = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
    const img = item.image || '';
    const brand = item.vendor || '';
    /* WEGLATEN, NIET VERBERGEN (ROADBLOCKS #18): op een afgeschermde regel komt er
       geen bedrag in de HTML — geen data-money, geen data-price, geen tekst. */
    const state = gateOf(item);
    let priceHtml;
    if (state) {
      priceHtml = `<div class="line__price">${gateLink(state, 'price__login')}</div>`;
    } else {
      /* /cart.js levert HELE CENTEN. Die gaan rechtstreeks door Money.fromCents;
         een omweg via euro's (/100) zou een drijvende-kommagetal introduceren
         waar de bron al exact is. */
      const final = item.final_line_price;
      const original = item.original_line_price;
      const showStrike = original > final;
      priceHtml = `<div class="line__price">${showStrike ? `<s>${Money.fromCents(original)}</s>` : ''}${Money.fromCents(final)}</div>`;
    }
    if (mini) {
      return `<div class="line">
        <div class="line__img"><img src="${img}" alt=""></div>
        <div class="line__main">
          <div class="line__name">${nameOnly}</div>
          ${variantLabel ? `<div class="line__variant">${variantLabel}</div>` : ''}
          <div class="line__row"><span class="muted" style="font-size:.8rem">${item.quantity} ×</span>${priceHtml}</div>
        </div>
      </div>`;
    }
    return `<div class="line">
      <div class="line__img"><img src="${img}" alt=""></div>
      <div class="line__main">
        <div class="line__brand">${brand}</div>
        <div class="line__name">${nameOnly}</div>
        ${variantLabel ? `<div class="line__variant">${variantLabel}</div>` : ''}
        <div class="line__row">
          <div class="qty">
            <button data-step="-1" data-cart-key="${item.key}" aria-label="${T('common.decrease', 'Minder')}">–</button>
            <input value="${item.quantity}" readonly aria-label="${T('common.quantity', 'Aantal')}">
            <button data-step="1" data-cart-key="${item.key}" aria-label="${T('common.increase', 'Meer')}">+</button>
          </div>
          ${priceHtml}
        </div>
        <button class="line__remove" data-cart-remove="${item.key}">${T('cart.remove', 'Verwijderen')}</button>
      </div>
    </div>`;
  }

  /* ---------- render header count / mini / drawer ---------- */
  function renderCount() {
    const n = OlmiaCart.count();
    $$('[data-cart-count]').forEach(el => { el.textContent = n; el.classList.toggle('has-items', n > 0); });
    $$('[data-cart-count-label]').forEach(el => el.textContent = `(${n})`);
  }
  function bump() { const el = $('[data-cart-count]'); if (el) { el.classList.remove('bump'); void el.offsetWidth; el.classList.add('bump'); } }

  function renderMini() {
    const box = $('[data-mini-cart]'); if (!box) return;
    const cart = OlmiaCart.cart;
    if (!cart || !cart.items.length) {
      box.innerHTML = `<div class="mini-cart__empty">${T('cart.mini_empty', 'Je winkelwagen is nog leeg.')}</div>`; return;
    }
    /* Eén afgeschermde regel schermt het hele subtotaal af: een totaal dat een
       afgeschermd bedrag bevat is zelf een lek. Afrekenen wordt dan de poort-CTA. */
    const state = cartGate(cart.items);
    const c = state ? gateCopy(state) : null;
    const subHtml = state ? gateLink(state, 'price__login') : Money.fromCents(cart.items_subtotal_price);
    const ctaHtml = state
      ? `<a href="${esc(c.href)}" class="btn btn--primary btn--block btn--sm">${esc(c.cta)}</a>`
      : `<a href="/checkout" class="btn btn--primary btn--block btn--sm">${T('cart.checkout', 'Afrekenen')}</a>`;
    box.innerHTML = `<div class="mini-cart__items">${cart.items.map(l => lineMarkup(l, true)).join('')}</div>
      <div class="mini-cart__foot">
        <div class="mini-cart__sub"><span>${T('cart.subtotal', 'Subtotaal')}</span><span>${subHtml}</span></div>
        ${ctaHtml}
        <a href="/cart" class="btn btn--ghost btn--block btn--sm">${T('cart.view_cart', 'Bekijk winkelwagen')}</a>
      </div>`;
  }

  function renderDrawer() {
    const wrap = $('[data-drawer-items]'); if (!wrap) return;
    const foot = $('[data-drawer-foot]');
    const cart = OlmiaCart.cart;
    if (!cart || !cart.items.length) {
      wrap.innerHTML = `<div class="drawer__empty">${ICON.cartBig}<div><b>${T('cart.empty_title', 'Je winkelwagen is leeg')}</b><p class="muted" style="font-size:.85rem">${T('cart.empty_body', 'Ontdek onze cobots, grippers en meer.')}</p></div><a href="/collections/alle-producten" class="btn btn--dark" data-close-cart>${T('cart.start_shopping', 'Start met winkelen')}</a></div>`;
      if (foot) foot.style.display = 'none';
      return;
    }
    if (foot) foot.style.display = '';
    wrap.innerHTML = cart.items.map(l => lineMarkup(l, false)).join('');

    /* Poort over de hele wagen: subtotaal + besparing vallen weg (niet: worden
       verborgen), Afrekenen maakt plaats voor de offerte-/inlog-CTA. Exact hetzelfde
       gedrag als sections/main-cart.liquid op /cart. */
    const state = cartGate(cart.items);
    const c = state ? gateCopy(state) : null;

    const sub = $('[data-cart-subtotal]');
    if (sub) {
      sub.textContent = state ? '' : Money.fromCents(cart.items_subtotal_price);
      sub.classList.toggle('hide', !!state);
    }
    const gateEl = $('[data-cart-gate]');
    if (gateEl) {
      gateEl.classList.toggle('hide', !state);
      if (c) { gateEl.textContent = c.label; gateEl.setAttribute('href', c.href); }
    }
    const sv = $('[data-cart-savings]');
    if (sv) sv.textContent = (!state && cart.total_discount > 0) ? T('cart.you_save', 'Je bespaart {amount}').replace('{amount}', () => Money.fromCents(cart.total_discount)) : '';
    const noteEl = $('[data-cart-gate-note]');
    if (noteEl) {
      noteEl.classList.toggle('hide', !state);
      if (c) noteEl.textContent = c.note || '';
    }
    const checkout = $('[data-drawer-checkout]');
    if (checkout) checkout.classList.toggle('hide', !!state);
    const gateCta = $('[data-drawer-gate-cta]');
    if (gateCta) {
      gateCta.classList.toggle('hide', !state);
      if (c) { gateCta.textContent = c.cta; gateCta.setAttribute('href', c.href); }
    }
  }

  function renderAll() { renderCount(); renderMini(); renderDrawer(); paintPrices(document); }

  /* ---------- toast ---------- */
  function toast(msg) {
    let wrap = $('.toast-wrap'); if (!wrap) { wrap = document.createElement('div'); wrap.className = 'toast-wrap'; document.body.appendChild(wrap); }
    const t = document.createElement('div'); t.className = 'toast'; t.innerHTML = `${ICON.check}<span>${msg}</span>`;
    wrap.appendChild(t); requestAnimationFrame(() => t.classList.add('show'));
    setTimeout(() => { t.classList.remove('show'); setTimeout(() => t.remove(), 350); }, 2400);
  }

  /* ---------- global interactions ---------- */
  function closeMobile() { $('#mobileMenu')?.classList.remove('open'); $('#mobileScrim')?.classList.remove('open'); document.body.style.overflow = ''; }

  function bindGlobal() {
    document.addEventListener('click', (e) => {
      // Scope to real quantity steppers only. A bare [data-step] also matches
      // .kh__step[data-step] (keuzehulp wrappers) and .bopt[data-step] (opstelling
      // option labels), which would throw / corrupt their radios on every click.
      const step = e.target.closest('.qty [data-step], [data-cart-key][data-step]');
      if (step) {
        const key = step.getAttribute('data-cart-key'); const d = +step.getAttribute('data-step');
        if (key) {
          const input = step.parentElement.querySelector('input');
          const cur = input ? (+input.value || 1) : 1;
          OlmiaCart.change(key, Math.max(0, cur + d));
        } else {
          const inp = step.parentElement.querySelector('input');
          inp.value = Math.max(1, (+inp.value || 1) + d);
          inp.dispatchEvent(new Event('input', { bubbles: true }));
        }
        return;
      }
      const rm = e.target.closest('[data-cart-remove]'); if (rm) { OlmiaCart.change(rm.getAttribute('data-cart-remove'), 0); return; }
      const add = e.target.closest('[data-add]'); if (add) { e.preventDefault(); OlmiaCart.add(add.getAttribute('data-add')).then(() => bump()); return; }
      if (e.target.closest('[data-open-cart]')) { e.preventDefault(); OlmiaCart.open(); return; }
      if (e.target.closest('[data-close-cart]')) { OlmiaCart.close(); return; }
      const accH = e.target.closest('.acc__head');
      if (accH) {
        const item = accH.parentElement, wasOpen = item.classList.contains('open');
        (item.closest('.acc') || document).querySelectorAll('.acc__item.open').forEach(x => x.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
        return;
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { OlmiaCart.close(); closeMobile(); } });
  }

  /* ---------- header (sticky, mobile menu) ---------- */
  // The sticky-scroll handler is bound to window exactly ONCE and resolves #siteHeader
  // LAZILY on each scroll. So after any number of header re-renders in the editor it keeps
  // toggling .is-stuck on the CURRENT header node (never a stale captured one), and there
  // is never more than one scroll listener however many times the header re-renders.
  let headerScrollBound = false;
  function stickHeader() {
    const h = document.getElementById('siteHeader');
    if (h) h.classList.toggle('is-stuck', window.scrollY > 8);
  }
  function bindHeader(root) {
    root = root || document;
    if (!headerScrollBound) {
      headerScrollBound = true;
      window.addEventListener('scroll', stickHeader, { passive: true });
    }
    stickHeader(); // re-evaluate the stuck state for the fresh header after a re-render
    const q = (s) => root.querySelector(s) || document.getElementById(s.slice(1));
    // per-node dedup (adversarial finding H1): a same-node repeat dispatch must not
    // stack listeners; fresh nodes after a real editor re-render carry no flag.
    const once = (el, fn) => { if (el && el.dataset.uiBound !== '1') { el.dataset.uiBound = '1'; fn(el); } };
    once(q('#burgerBtn'), (el) => el.addEventListener('click', () => { $('#mobileMenu').classList.add('open'); $('#mobileScrim').classList.add('open'); document.body.style.overflow = 'hidden'; }));
    once(q('#mobileClose'), (el) => el.addEventListener('click', closeMobile));
    once(q('#mobileScrim'), (el) => el.addEventListener('click', closeMobile));
  }
  // The single lazy scroll listener is safe to keep across re-renders (it no-ops when no
  // #siteHeader is present), so a per-section teardown has nothing to remove.
  function teardownHeader() {}

  /* ---------- announce-bar rotation (mobile only, 5s) ---------- */
  // Element-agnostic timer handle (not a latched boolean) so the rotation can be
  // restarted on the fresh #announceBar after an editor re-render — clearing the
  // stale interval first so removed spans are never toggled and timers never stack.
  let announceTimer = null;
  function teardownAnnounce() { if (announceTimer) { clearInterval(announceTimer); announceTimer = null; } }
  function initAnnounce(root) {
    root = root || document;
    teardownAnnounce();
    const bar = root.querySelector('#announceBar') || document.getElementById('announceBar');
    if (!bar || !window.matchMedia('(max-width: 720px)').matches) return;
    // .announce-extra messages are desktop-only ("Alleen op desktop tonen"): keep
    // them out of the mobile rotation so they never become .is-active (= visible)
    // on mobile, where only the active span is shown.
    const all = [...bar.querySelectorAll('span')];
    all.forEach(s => { if (s.classList.contains('announce-extra')) s.classList.remove('is-active'); });
    const spans = all.filter(s => !s.classList.contains('announce-extra'));
    if (spans.length < 2) return;
    let i = Math.max(0, spans.findIndex(s => s.classList.contains('is-active')));
    spans.forEach((s, n) => s.classList.toggle('is-active', n === i));
    announceTimer = setInterval(() => {
      spans[i].classList.remove('is-active');
      i = (i + 1) % spans.length;
      spans[i].classList.add('is-active');
    }, 5000);
  }

  /* ---------- expert chat (Jan Willem, canned demo replies) ---------- */
  let chatStarted = false;
  function initChat() {
    if (chatStarted) return;
    const launcher = document.getElementById('chatLauncher');
    const panel = document.getElementById('chatPanel');
    const body = document.getElementById('chatBody');
    if (!launcher || !panel) return;
    chatStarted = true;

    // HET BEDRAG IN `replies.prijs` (beoordeeld 2026-08-18, bewust NIET gewijzigd)
    // Deze vier antwoorden zijn verbatim overgenomen uit het statische design:
    // Cobotwebshop-designreferentie-voor-Sean/assets/js/chat.js regel 5-9. Het is een
    // indicatie in VERKOOPTEKST, geen catalogusprijs: het bedrag komt in de winkel bij
    // geen enkele variant voor en is ook geen klantprijs (nagemeten tegen de Admin API
    // op 2026-08-18). Het is dus geen prijslek uit de catalogus, maar het is wél het
    // enige euro-bedrag in een publiek CDN-asset terwijl elk ander prijsoppervlak
    // achter de muur staat; `scripts/verify-asset-leak.mjs` check B meldt het terecht.
    // Wie het wil opruimen: naar een sectie-instelling (snippets/chat-widget.liquid)
    // tillen zodat Olmia de tekst zelf beheert. Dat is een MERCHANT-besluit over
    // verkoopcopy, geen bugfix — daarom gemeld in plaats van stil veranderd.
    // LET OP: zet hier nooit een tweede bedrag in, ook niet als toelichting in een
    // comment. Comments staan gewoon in het gepubliceerde asset (ROADBLOCKS #27).
    const replies = {
      palletiseren: T('cart.chat_reply_pallet', 'Voor palletiseren is onze eigen <b>Palletbooster</b> meestal de beste route: een complete cel met cobot, gripper en software, draaiend opgeleverd. Liever losse componenten? Dan denk ik mee over een <b>UR15</b> met <b>VGP30</b>-gripper. Zal ik de Palletbooster laten zien?'),
      demo: T('cart.chat_reply_demo', 'Top! 🙌 Een vrijblijvende demo regel ik zo. Op welke locatie en met welk type product werk je? Dan stem ik de demo daarop af.'),
      prijs: 'Een complete UR15-opstelling met gripper, onderstel en installatie start rond <b>€ 52.000</b>. Met de bundel-bouwer op de productpagina zie je direct een meelopend totaal.',
      default: T('cart.chat_reply_default', 'Goede vraag! Ik denk graag met je mee. Wil je dat ik je bel, of zal ik een paar opties op een rij zetten?'),
    };
    function match(text) {
      const t = text.toLowerCase();
      if (/pallet|stapel|dozen|zakken|stack|boxes|bags/.test(t)) return replies.palletiseren;
      if (/demo|afspraak|langskomen|bezoek|appointment|visit/.test(t)) return replies.demo;
      if (/prijs|kost|budget|euro|€|price|cost|quote/.test(t)) return replies.prijs;
      return replies.default;
    }

    const open = () => { panel.classList.add('open'); launcher.classList.add('hidden'); setTimeout(() => document.getElementById('chatText')?.focus(), 200); };
    const close = () => { panel.classList.remove('open'); launcher.classList.remove('hidden'); };
    launcher.addEventListener('click', open);
    document.getElementById('chatClose')?.addEventListener('click', close);

    function push(text, me) {
      const b = document.createElement('div');
      b.className = 'bubble ' + (me ? 'bubble--me' : 'bubble--bot');
      b.innerHTML = text;
      body.appendChild(b); body.scrollTop = body.scrollHeight;
    }
    function botReply(text) {
      const typing = document.createElement('div');
      typing.className = 'bubble bubble--bot'; typing.textContent = '…';
      body.appendChild(typing); body.scrollTop = body.scrollHeight;
      setTimeout(() => { typing.remove(); push(match(text), false); }, 850);
    }
    function send(text) { if (!text.trim()) return; push(text, true); document.getElementById('chatQuick')?.style.setProperty('display', 'none'); botReply(text); }

    document.getElementById('chatForm')?.addEventListener('submit', (e) => {
      e.preventDefault(); const inp = document.getElementById('chatText'); send(inp.value); inp.value = '';
    });
    document.querySelectorAll('#chatQuick button').forEach(b => b.addEventListener('click', () => send(b.dataset.q)));
  }

  /* ---------- init ---------- */
  let booted = false;
  function init() {
    bindHeader();
    if (!booted) {
      bindGlobal();
      if (window.OlmiaCart) OlmiaCart.subscribe(renderAll);
      booted = true;
    }
    renderAll();
    initAnnounce();
    initChat();
  }

  /* ---------- theme-editor re-init (header + announce sections) ---------- */
  if (window.OlmiaEditor) {
    OlmiaEditor.register({
      key: 'olmia-ui-header',
      match: (r) => !!(r && r.querySelector && (r.querySelector('#siteHeader') || r.querySelector('#burgerBtn'))),
      init: (root) => { bindHeader(root); renderCount(); paintPrices(root); },
      teardown: () => teardownHeader()
    });
    OlmiaEditor.register({
      key: 'olmia-ui-announce',
      match: (r) => !!(r && r.querySelector && r.querySelector('#announceBar')),
      init: (root) => initAnnounce(root),
      teardown: () => teardownAnnounce()
    });
  }

  /* `Money` staat bewust in de publieke API: elk ander asset moet hem gebruiken
     in plaats van een eigen Intl.NumberFormat te bouwen (zie PRICE RULE boven). */
  return { init, paintPrices, toast, nlNum, Money, lineMarkup, ICON, $, $$ };
})();

if (document.readyState !== 'loading') OlmiaUI.init();
else document.addEventListener('DOMContentLoaded', () => OlmiaUI.init());
